/**
 * StudioMessagingConfigTab · iter138
 *
 * Creator konfiguriert:
 *  - free_for_all Toggle (jeder darf schreiben, keine Quota)
 *  - Tier-Kontingente (Standard/Premium/VIP: Nachrichten pro 30 Tage)
 *  - Nachrichten-Pakete (Einzelkauf: N Nachrichten für X €)
 *
 * API: GET/PUT /api/studio/messaging/config
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Trash2, MessageCircle, Info, Save } from "lucide-react";
import api from "@/lib/api";

const DEFAULT_CFG = {
  // iter229 · Monetarisierungs-Defaults (Msg 853) — spiegeln Backend-Seed für
  // Creator, die die Config noch nie geöffnet haben.
  free_for_all: false,
  first_message_free: true,
  tier_quotas: { standard: 150, premium: 150, vip: 150 },
  packages: [],
};

export default function StudioMessagingConfigTab() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/studio/messaging/config");
        setCfg({ ...DEFAULT_CFG, ...(data?.config || {}) });
      } catch (e) { setError(t("studio_msg.load_error", { defaultValue: "Konfiguration konnte nicht geladen werden." })); }
      finally { setLoading(false); }
    })();
  }, [t]);

  const updateTier = (key, value) => {
    const n = Math.max(0, Math.min(10000, Number(value) || 0));
    setCfg((c) => ({ ...c, tier_quotas: { ...c.tier_quotas, [key]: n } }));
  };

  const addPackage = () => {
    setCfg((c) => ({ ...c, packages: [...(c.packages || []), { label: "", messages: 10, price_eur: 5 }] }));
  };
  const updatePackage = (idx, field, value) => {
    setCfg((c) => {
      const pkgs = [...(c.packages || [])];
      pkgs[idx] = { ...pkgs[idx], [field]: field === "label" ? value : Math.max(1, Number(value) || 1) };
      return { ...c, packages: pkgs };
    });
  };
  const removePackage = (idx) => {
    setCfg((c) => ({ ...c, packages: (c.packages || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = {
        free_for_all: !!cfg.free_for_all,
        first_message_free: !!cfg.first_message_free, // iter172d
        tier_quotas: {
          standard: Number(cfg.tier_quotas?.standard || 0),
          premium: Number(cfg.tier_quotas?.premium || 0),
          vip: Number(cfg.tier_quotas?.vip || 0),
        },
        packages: (cfg.packages || []).filter((p) => p.label && p.messages > 0 && p.price_eur > 0),
      };
      await api.put("/studio/messaging/config", body);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || t("studio_msg.save_error", { defaultValue: "Speichern fehlgeschlagen." }));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-[#999]" /></div>;

  return (
    <div data-testid="studio-msg-config" className="p-4 space-y-6 max-w-2xl">
      {/* Info-Box */}
      <div className="rounded-2xl bg-[#FAFAFA] border border-[#E5E5E5] p-3 text-xs text-[#666] flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0" />
        <p>{t("studio_msg.info", { defaultValue: "Lege fest, wer dich direkt anschreiben darf und in welchem Umfang. Kontingente rollen 30 Tage. Pakete gelten unbegrenzt bis verbraucht." })}</p>
      </div>

      {/* P6 · Hinweis: bezahlbare Nachrichten gelten nur für stilltouchedX. Social ist immer kostenlos. */}
      <div data-testid="studio-msg-stx-hint" className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0" />
        <p>{t("studio_msg.stx_only", { defaultValue: "Diese Einstellungen gelten ausschließlich für stilltouchedX-Nachrichten. Nachrichten im Social-Bereich sind immer kostenlos." })}</p>
      </div>

      {/* Free-for-all Toggle */}
      <button
        data-testid="studio-msg-free-toggle"
        onClick={() => setCfg((c) => ({ ...c, free_for_all: !c.free_for_all }))}
        className="w-full flex items-center gap-3 rounded-2xl border border-[#E5E5E5] p-3 text-left hover:bg-[#FAFAFA]">
        <div className={`w-11 h-6 rounded-full transition-colors ${cfg.free_for_all ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"} relative`}>
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cfg.free_for_all ? "translate-x-5" : "translate-x-0.5"}`} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#0A0A0A]">{t("studio_msg.free_title", { defaultValue: "Für alle offen" })}</p>
          <p className="text-xs text-[#666]">{t("studio_msg.free_body", { defaultValue: "Jeder darf dich ohne Limit anschreiben. Deaktiviert Tier-Kontingente." })}</p>
        </div>
      </button>

      {/* iter172d · „Erste Nachricht gratis"-Toggle (nur wenn free_for_all=false) */}
      {!cfg.free_for_all && (
        <button
          data-testid="studio-msg-first-free-toggle"
          onClick={() => setCfg((c) => ({ ...c, first_message_free: !c.first_message_free }))}
          className="w-full flex items-center gap-3 rounded-2xl border border-[#E5E5E5] p-3 text-left hover:bg-[#FAFAFA]">
          <div className={`w-11 h-6 rounded-full transition-colors ${cfg.first_message_free ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"} relative`}>
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cfg.first_message_free ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#0A0A0A]">{t("studio.first_message_free")}</p>
            <p className="text-xs text-[#666]">
              Interessenten dürfen dich einmalig kostenlos anschreiben — senkt die Hürde für Erst-Kontakte und erhöht die Conversion zu zahlenden Abonnenten.
            </p>
          </div>
        </button>
      )}

      {/* iter164 · Nur noch EIN Kontingent (Premium-Abo Single-Tier) */}
      <div className={cfg.free_for_all ? "opacity-40 pointer-events-none" : ""}>
        <h2 className="text-sm font-bold text-brand-black mb-2 flex items-center gap-2">
          <MessageCircle size={14} /> {t("studio_msg.quota_title", { defaultValue: "Kontingent" })}
        </h2>
        <p className="text-xs text-[#666] mb-3">{t("studio_msg.quota_body", { defaultValue: "Wieviele Nachrichten darf ein Premium-Abonnent dir pro 30 Tage senden?" })}</p>
        <div className="grid grid-cols-1 sm:max-w-xs">
          <div className="rounded-2xl border border-[#E5E5E5] p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Premium-Abo</p>
            <input
              data-testid="studio-msg-tier-premium"
              type="number" min={0} max={10000}
              placeholder="150"
              value={cfg.tier_quotas?.premium || 0}
              onChange={(e) => {
                // iter164 · Alle drei Tier-Felder synchron halten (BE-Kompatibilität)
                const n = Math.max(0, Math.min(10000, Number(e.target.value) || 0));
                setCfg((c) => ({ ...c, tier_quotas: { standard: n, premium: n, vip: n } }));
              }}
              className="w-full h-10 rounded-full border border-[#E5E5E5] px-3 text-sm font-bold text-center focus:border-brand-black focus:outline-none"
            />
            <p className="text-[10px] text-[#999] mt-1 text-center">{t("studio.messages_per_30_days")}</p>
          </div>
        </div>
      </div>

      {/* Pakete */}
      <div className={cfg.free_for_all ? "opacity-40 pointer-events-none" : ""}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-bold text-[#0A0A0A] flex items-center gap-2">
            <MessageCircle size={14} /> {t("studio_msg.packages_title", { defaultValue: "Nachrichten-Pakete (Einzelkauf)" })}
          </h2>
          <button
            data-testid="studio-msg-add-pkg"
            onClick={addPackage}
            className="h-8 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-bold flex items-center gap-1 hover:bg-[#222]">
            <Plus size={12} /> {t("studio_msg.add_pkg", { defaultValue: "Paket" })}
          </button>
        </div>
        <p className="text-xs text-[#666] mb-3">{t("studio_msg.packages_body", { defaultValue: "Nicht-Abonnenten oder erschöpfte Abonnenten können diese Pakete kaufen." })}</p>
        {(cfg.packages || []).length === 0 ? (
          <p className="text-center text-xs text-[#999] py-4">{t("studio_msg.no_pkg", { defaultValue: "Noch keine Pakete definiert." })}</p>
        ) : (
          <ul className="space-y-2">
            {(cfg.packages || []).map((pkg, i) => (
              <li key={i} data-testid={`studio-msg-pkg-${i}`} className="grid grid-cols-12 gap-2 items-center rounded-2xl border border-[#E5E5E5] p-2">
                <input
                  data-testid={`studio-msg-pkg-label-${i}`}
                  type="text" maxLength={80}
                  value={pkg.label || ""}
                  onChange={(e) => updatePackage(i, "label", e.target.value)}
                  placeholder={t("studio_msg.pkg_label_ph", { defaultValue: "Bezeichnung" })}
                  className="col-span-5 h-9 rounded-full border border-[#E5E5E5] px-3 text-xs focus:border-[#0A0A0A] focus:outline-none"
                />
                <div className="col-span-3 flex items-center gap-1">
                  <input
                    data-testid={`studio-msg-pkg-msgs-${i}`}
                    type="number" min={1} max={10000}
                    value={pkg.messages || 0}
                    onChange={(e) => updatePackage(i, "messages", e.target.value)}
                    className="w-full h-9 rounded-full border border-[#E5E5E5] px-3 text-xs text-center focus:border-[#0A0A0A] focus:outline-none"
                  />
                  <span className="text-[10px] text-[#999] whitespace-nowrap">Msg</span>
                </div>
                <div className="col-span-3 flex items-center gap-1">
                  <input
                    data-testid={`studio-msg-pkg-price-${i}`}
                    type="number" min={1} max={500} step={0.5}
                    value={pkg.price_eur || 0}
                    onChange={(e) => updatePackage(i, "price_eur", e.target.value)}
                    className="w-full h-9 rounded-full border border-[#E5E5E5] px-3 text-xs text-center focus:border-[#0A0A0A] focus:outline-none"
                  />
                  <span className="text-[10px] text-[#999]">€</span>
                </div>
                <button
                  data-testid={`studio-msg-pkg-remove-${i}`}
                  onClick={() => removePackage(i)}
                  className="col-span-1 h-9 rounded-full text-[#999] hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <div data-testid="studio-msg-error" className="rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">{error}</div>
      )}
      {savedFlash && (
        <div data-testid="studio-msg-saved" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          {t("studio_msg.saved", { defaultValue: "Gespeichert." })}
        </div>
      )}

      <button
        data-testid="studio-msg-save"
        onClick={save}
        disabled={saving}
        className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#222] disabled:opacity-40">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {t("studio_msg.save", { defaultValue: "Konfiguration speichern" })}
      </button>
    </div>
  );
}
